import {InlineMenu, InlineMenuControl} from "../index";
import eventListenerOption from "../../../scripts/eventListenerOption";
import ComponentManager from "../../../componentFramework";

interface MenuInstanceMetadata {
	menu: InlineMenu;
	rootElement: Element;
	originalParentElement: Element | null;
	doOpen: (control: InlineMenuControl) => void;
	doClose: () => void;
}

interface ControlInstanceMetadata {
	control: InlineMenuControl;
	doOpen: () => void;
	doClose: () => void;
}

export default class InlineMenuManager {
	private static instance: InlineMenuManager;

	private static readonly containerStyleClass = "inlineMenus";

	// There can be multiple controls per id, but only one menu per id
	private readonly controlInstances = new Map<string, ControlInstanceMetadata[]>();
	private readonly menuInstances = new Map<string, MenuInstanceMetadata>();

	private openMenuId: string | null = null;

	static getInstance(): InlineMenuManager {
		// Note: Having a method vs static method lets us lazy init but also potentially add "groups" of controllers and menus in the future.
		if (InlineMenuManager.instance === undefined) {
			return InlineMenuManager.instance = new InlineMenuManager();
		}
		return InlineMenuManager.instance;
	}

	private constructor() {
		const onEventToCauseClose = (event: Event) => {
			if (event.isTrusted) {
				// true when the event was generated by a user action, and false when the event was created
				//  or modified by a script or dispatched via EventTarget.dispatchEvent() or target.click()
				// Note: Not supported by IE, but we don't care. If we needed IE11 support we could probably check
				//  other data like if the click was at 0,0
				this.close();
			}
		};

		// We don't need to use an EventListenerManager here because once this InlineMenuManager instance has been created, it won't be destroyed until the page is unloaded entirely
		window.addEventListener("scroll", onEventToCauseClose, eventListenerOption.passiveCapture);
		window.addEventListener("resize", onEventToCauseClose, eventListenerOption.passiveCapture);
		window.addEventListener("click", onEventToCauseClose, eventListenerOption.passiveCapture);

		window.addEventListener("keydown", event => {
			if (this.openMenuId !== null && (event.key === "Escape" || event.key === "Esc")) {
				this.close();
				event.stopImmediatePropagation();
			}
		});
	}

	public open(control: InlineMenuControl) {
		const menuId = control.getMenuId();

		if (this.openMenuId !== menuId) {
			this.close();
		}

		const menuInstance = this.menuInstances.get(menuId);
		if (menuInstance !== undefined) {
			menuInstance.doOpen.call(menuInstance.menu, control);

			const controlInstances = this.controlInstances.get(menuId);
			if (controlInstances !== undefined) {
				const instance = controlInstances.find(instance => instance.control === control);
				instance?.doOpen.call(instance.control);
			}

			this.openMenuId = menuId;
			return true;
		}
		return false;
	}
	public close(menuId?: string) {
		if (this.openMenuId !== null && (menuId === undefined || menuId === this.openMenuId)) {
			const menuInstance = this.menuInstances.get(this.openMenuId);
			if (menuInstance !== undefined) {
				menuInstance.doClose.call(menuInstance.menu);
				this.controlInstances.get(this.openMenuId)?.forEach(controlInstance => controlInstance.doClose.call(controlInstance.control));

				this.openMenuId = null;
				return true;
			}
		}
		return false;
	}

	public registerControl(control: InlineMenuControl, doOpen: () => void, doClose: () => void) {
		let instances = this.controlInstances.get(control.getMenuId());
		if (instances === undefined) {
			this.controlInstances.set(control.getMenuId(), [{control, doOpen, doClose}]);
		} else {
			instances.push({control, doOpen, doClose});
		}
	}
	public unregisterControl(control: InlineMenuControl): boolean {
		const instances = this.controlInstances.get(control.getMenuId());
		if (instances === undefined) {
			return false;
		}
		const controlIndex = instances.findIndex(instance => instance.control === control);
		if (controlIndex === -1) {
			return false;
		}
		instances.splice(controlIndex, 1);
		if (instances.length === 0) {
			this.controlInstances.delete(control.getMenuId());
		}
		return true;
	}
	public getControls(menuId: string): InlineMenuControl[] | undefined {
		return this.controlInstances.get(menuId)?.map(instance => instance.control);
	}

	public registerMenu(menu: InlineMenu, rootElement: Element, doOpen: (control: InlineMenuControl) => void, doClose: () => void) {
		const menuId = menu.getId();
		const originalParentElement = rootElement.parentElement;

		// If a menu exists with the same id, detach it from the DOM and unregister it
		const existing = this.menuInstances.get(menuId);
		if (existing !== undefined) {
			this.unregisterMenu(existing.menu);
			existing.rootElement.remove();
		}
		this.menuCleanup(); // Check if anything else needs to be cleaned up.

		// Move this element into the container
		InlineMenuManager.getContainer(rootElement.ownerDocument).appendChild(rootElement);

		// Store a reference
		this.menuInstances.set(menuId, {menu, doOpen, doClose, rootElement, originalParentElement});
	}

	/**
	 * This removes any registered menu instances where the original parent no longer exists
	 * or is no longer attached to the DOM.
	 * We only run this when new menus are registered, so there could be some left, but it seems
	 * often enough.
	 */
	private menuCleanup() {
		this.menuInstances.forEach(instance => {
			if (instance.originalParentElement === null || !instance.originalParentElement.isConnected) {
				this.unregisterMenu(instance.menu);
				instance.rootElement.remove();
			}
		});
	}

	public unregisterMenu(menu: InlineMenu): boolean {
		const instance = this.menuInstances.get(menu.getId());
		if (instance !== undefined && instance.menu === menu) {
			if (menu.getId() === this.openMenuId) {
				this.close();
			}
			return this.menuInstances.delete(menu.getId());
		}
		return false;
	}

	public getMenu(menuId: string): InlineMenu | undefined {
		return this.menuInstances.get(menuId)?.menu;
	}

	public isControlOrMenuElement(element: Element) {
		for (let value of this.menuInstances.values()) {
			if (value.rootElement.contains(element)) {
				return true;
			}
		}
		for (let value of this.controlInstances.values()) {
			if (value.some(controlInstance => controlInstance.control.getRootElement().contains(element))) {
				return true;
			}
		}
		return false;
	}

	/**
	 * We place all InlineMenu instances inside a container which we make
	 * as child of body. This allows us to solve z-index, overflow: hidden,
	 * and other possible issue related to where InlineMenu make exist by default
	 * in the document.
	 */
	private static getContainer(doc: Document): Element {
		let containerElement = doc.querySelector("." + InlineMenuManager.containerStyleClass);
		if (containerElement === null) {
			containerElement = doc.createElement("div");
			ComponentManager.getInstance(doc).ignoreElement(containerElement, true, true);
			containerElement.classList.add(InlineMenuManager.containerStyleClass);
			doc.body.appendChild(containerElement);
		}
		return containerElement;
	}
}
